import { t } from "i18next";
import HelpButton from "./components/pages/auth-pages/HelpButton";
import AuthHeading from "./components/pages/auth-pages/AuthHeading";
import AuthSubHeading from "./components/pages/auth-pages/AuthSubHeading";
import AuthInput from "./components/pages/auth-pages/AuthInput";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import useAuth from "@/utils/hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation } from "@/data/user";
import * as yup from "yup";
import { LoginUserInput } from "@/types";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import routes from "@/config/routes";
import { useSEO } from "@/contexts/SeoContext";
import "../../styles/globals.css";

const LoginV3 = ({ redirect = true }: { redirect?: boolean }) => {
  const { setTitle, setDescription } = useSEO();

  useEffect(() => {
    setTitle("Login");
    setDescription("Login to your account");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const { authorize } = useAuth();
  const navigate = useNavigate();
  const { mutate: login, isLoading } = useLoginMutation();

  const loginValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("auth.errors.valid_email"))
      .required(t("auth.errors.email_required")),
    password: yup.string().required(t("auth.errors.password_required")),
  });

  const handleSubmit = (data: LoginUserInput) => {
    loginValidationSchema
      .validate({ email, password })
      .then(() => {
        login(data, {
          onSuccess: (response) => {
            if (response.status !== 200) {
              enqueueSnackbar("Something went wrong. Please retry", {
                variant: "error",
              });
              return;
            }

            if (!response.item.user.created_account) {
              enqueueSnackbar("Complete your registration", {
                variant: "warning",
                action: (key) => (
                  <Link
                    to={routes.register.replace(
                      ":token",
                      response.item.user.token,
                    )}
                    onClick={() => closeSnackbar(key)}
                    className="text-brand font-semibold bg-white cursor-pointer px-2 py-1 rounded-md"
                  >
                    Click here
                  </Link>
                ),
              });
              return;
            }

            enqueueSnackbar("You have successfully logged in", {
              variant: "success",
            });
            authorize(response.item.token);
            if (redirect) {
              navigate(routes.home);
            }
          },
          onError: () => {
            enqueueSnackbar(
              "Login failed. Please check your credentials and try again.",
              { variant: "error" },
            );
          },
        });
      })
      .catch((error: Error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      });
  };

  return (
    <main className="w-full h-full bg-login-bg flex items-center justify-center md:p-[40px] lg:p-[58px] bg-cover">
      <div className="w-full h-full flex flex-col">
        <div className="flex flex-row justify-end">
          <HelpButton />
        </div>
        <div className="flex h-full flex-col w-full justify-end xl:justify-center items-center">
          <div className="flex flex-col items-center justify-center gap-10">
            <div className="flex flex-col items-center justify-center gap-5">
              <AuthHeading
                text={t("v3.auth.login_header", {
                  defaultValue: "Hello, nice to see you! 👋",
                })}
              />
              <AuthSubHeading
                text={t("v3.auth.login_sub_header", {
                  defaultValue:
                    "Enter your email address and password to login",
                })}
              />
            </div>
            <div className="flex flex-col items-center justify-center gap-5">
              <AuthInput
                placeholder="Email address"
                type="email"
                name="login-email"
                handleChange={setEmail}
                regex={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
              />
              <AuthInput
                placeholder="Password"
                type="password"
                name="login-password"
                handleChange={setPassword}
                regex={/^.+$/}
              />
              <div className="flex w-full justify-end">
                <button
                  className="text-white hover:underline text-sm"
                  onClick={() => {
                    // TODO-V3: navigate to forget password page
                    navigate(routes.resetV3);
                  }}
                >
                  {t("v3.auth.forgot_password", {
                    defaultValue: "Forgot password 🔑",
                  })}
                </button>
              </div>
            </div>
            <Button
              isLoading={isLoading}
              className="text-sm rounded-[10px] md:mt-[25vh] xl:mt-0 w-full xl:w-[200px] font-semibold h-10 hover:bg-[#6941C6]"
              onClick={() => handleSubmit({ email: email, password: password })}
              type="button"
            >
              {t("v3.btns.login", { defaultValue: "Login" })}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LoginV3;
